import { useMemo } from 'react';
import { useField } from 'formik';
import i18n from 'i18n';
import NativeSelect from 'components/common/Select/Select';
import styles from './outdoorFields.module.scss';

const SECONDS_PER_MINUTE = 60;

const rangeOptions = (max: number) =>
  Array.from({ length: max + 1 }, (_, value) => ({
    label: String(value),
    value
  }));

/**
 * 分與秒兩個下拉，合起來編輯同一個總秒數欄位。
 *
 * 值的唯一來源是 `name` 指到的那個欄位（秒），元件自己不持有第二份狀態 ——
 * 送出去的形狀因此不會多出分與秒兩個欄位。
 */
export const OutdoorDurationSelects: React.FC<{
  name: string;
  /** 分的選項上界由它換算而來；秒一律 0–59。 */
  maxSeconds?: number;
  validate?: (value: number) => string | undefined;
}> = ({ name, maxSeconds = 0, validate }) => {
  const [{ value }, { error, touched }, { setValue, setTouched }] = useField({
    name,
    validate
  });

  const totalSeconds = Number(value) || 0;
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  const minuteOptions = useMemo(
    () => rangeOptions(Math.floor(maxSeconds / SECONDS_PER_MINUTE)),
    [maxSeconds]
  );
  const secondOptions = useMemo(() => rangeOptions(SECONDS_PER_MINUTE - 1), []);

  const onChange = (nextMinutes: number, nextSeconds: number) => {
    setTouched(true, false);
    setValue(nextMinutes * SECONDS_PER_MINUTE + nextSeconds);
  };

  return (
    <div className={styles.durationSelects}>
      <div className={styles.durationSelect}>
        <NativeSelect
          name={`${name}.minutes`}
          labelKey='label'
          valueKey='value'
          simpleValue
          componentWidthFitParent
          options={minuteOptions}
          value={minutes}
          onChange={(nextMinutes) => onChange(Number(nextMinutes), seconds)}
        />
      </div>
      <span className={styles.durationUnit}>
        {i18n.t('common.units.minutes')}
      </span>
      <div className={styles.durationSelect}>
        <NativeSelect
          name={`${name}.seconds`}
          labelKey='label'
          valueKey='value'
          simpleValue
          componentWidthFitParent
          options={secondOptions}
          value={seconds}
          onChange={(nextSeconds) => onChange(minutes, Number(nextSeconds))}
        />
      </div>
      <span className={styles.durationUnit}>
        {i18n.t('common.units.seconds')}
      </span>
      {touched && error && (
        <div className={styles.durationSelectsError}>{error}</div>
      )}
    </div>
  );
};
